import { Box, Tooltip } from "@chakra-ui/react";
import { ColumnMeta, createColumnHelper, VisibilityState } from "@tanstack/react-table";
import { TResultScoreDetailsResults } from "api/useGetReports";
import ScoreBadge from "components/v2/ScoreBadge";
import { TInventoryOptionValue } from "constants/common";
import TAggregatorColumnPDF from "types/TAggregatorColumnPDF";
import TAggregatorColumnXLS from "types/TAggregatorColumnXLS";
import { resultScoreConverterToString } from "utils/helper";
import { GoQuestion } from "react-icons/go";
import { Text } from "@chakra-ui/react";
import { TDealer } from "api/dealershipApi";

interface ResultScoreCustomHeaderProps {
  title: string;
  tooltip?: React.ReactNode;
}

export function ResultScoreCustomHeader(props: ResultScoreCustomHeaderProps) {
  return (
    <Box display="flex" alignItems="center" gap="5px" justifyContent="center">
      {props.title}

      <Tooltip
          label={props.tooltip}
          placement="top"
          hasArrow={true}
        >
          <Box>
            <GoQuestion />
          </Box>
      </Tooltip>
    </Box>
  );
}

const defaultMeta: ColumnMeta<TResultScoreDetailsResults, string> = {
  headerStyle: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  cellStyle: {
    textAlign: 'center',
  },
};

const columnHelper = createColumnHelper<TResultScoreDetailsResults>();

export const getColumns = (inventory: TInventoryOptionValue, dealer: TDealer) => [
  // columnHelper.accessor("vin", {
  //   cell: (info) => info.getValue() ?? '-',
  //   header: 'VIN',
  //   id: "vin",
  //   // default to unchecked
  // }),
  columnHelper.accessor("stock_number", {
    cell: (info) => info.getValue() ?? "-",
    header: "Stock #",
    id: "stock_number",
    meta: {
      ...defaultMeta,
    }
  }),
  columnHelper.accessor("vehicle", {
    id: "vehicle",
    cell: (info) => info.getValue() ?? "-",
    header: "Vehicle",
    size: 250,
    meta: {
      ...defaultMeta,
    }
  }),
  ...(inventory === 'used' ? [
      columnHelper.accessor("is_certified", {
          id: "is_certified",
          cell: (info) => info.getValue() ? 'Yes' : 'No',
          header: 'Certified',
          meta: {
            ...defaultMeta,
          }
      })
  ] : []),
  columnHelper.accessor("result_score", {
    cell: (info) => (
      <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
    ),
    header: "Result Score",
    id: "result_score",
    meta: {
      headerStyle: {
        ...defaultMeta.headerStyle,
        // backgroundColor: '#fef0c7'
        borderLeft: "3px solid #e2e8f0",
        borderRight: "3px solid #e2e8f0",
      },
      cellStyle: {
        ...defaultMeta.cellStyle,
        // backgroundColor: '#fef0c7'
        borderLeft: "3px solid #e2e8f0",
        borderRight: "3px solid #e2e8f0",
      },
      showHeaderTooltip: false,
      customHeaderRender: (
        <ResultScoreCustomHeader
          title="Result Score"
          tooltip={
            <Box
              display="flex"
              flexDirection="column"
              gap="10px"
              padding="5px"
            >
              <Text>
                Green if a vehicle is primed to sell quickly, meaning additional marketing spend is unnecessary.
              </Text>
              <Text>
                Yellow if a vehicle has areas for improvement that could enhance marketing effectiveness.
              </Text>
              <Text>
                Red if a vehicles is underperforming and requires strategic intervention to generate sales activity.
              </Text>
            </Box>
          }
        />
      ),
    }
  }),
  columnHelper.accessor("vdps_score", {
    cell: (info) => (
      <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
    ),
    header: `VDP's`,
    id: "vdps_score",
    ...(dealer.is_lite ? {
      meta: {
        ...defaultMeta,
        showHeaderTooltip: false,
        customHeaderRender: (
          <ResultScoreCustomHeader
            title="VDP's"
            tooltip={
              <Box
                display="flex"
                flexDirection="column"
                gap="10px"
                padding="10px"
              >
                <Text>
                  Green if vehicle has 5 or more vdps/dol.
                </Text>
                <Text>
                  Yellow if vehicle has between 3-4 vdps/dol.
                </Text>
                <Text>
                  Red if vehicle has less than 2 vdps/dol.
                </Text>
              </Box>
            }
          />
        ),
      },
    } : {
      meta: {
        ...defaultMeta,
      }
    })
  }),
  columnHelper.accessor("merchandising_score", {
    cell: (info) => (
      <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
    ),
    header: "Merchandising",
    id: "merchandising_score",
    ...(dealer.is_lite ? {
      meta: {
        ...defaultMeta,
        showHeaderTooltip: false,
        customHeaderRender: (
          <ResultScoreCustomHeader
            title="Merchandising"
            tooltip={
              <Box
                display="flex"
                flexDirection="column"
                gap="10px"
                padding="5px"
              >
                {inventory === "new" ? (
                  <>
                    <Text>
                      Green if a vehicle has a description, has price and 8 or more
                      photos.
                    </Text>
                    <Text>
                      Yellow if vehicle either has a description or price, and has
                      between 4-7 photos.
                    </Text>
                    <Text>
                      Red if vehicle is missing a description and price, and has 3
                      or less photos.
                    </Text>
                  </>
                ) : (
                  <>
                    <Text>
                      Green if a vehicle has a description, has price and 16 or more photos.
                    </Text>
                    <Text>
                      Yellow if vehicle either has a description or price, and has between 9-15 photos.
                    </Text>
                    <Text>
                      Red if vehicle is missing a description and price, and has 8 or less photos.
                    </Text>
                  </>
                )}
              </Box>
            }
          />
        ),
      },
    } : {
      meta: {
        ...defaultMeta,
      }
    })
  }),
  ...(inventory === "used"
    ? [
      columnHelper.accessor("deal_rating_score", {
        cell: (info) => (
          <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
        ),
        header: "Deal Rating",
        id: "deal_rating_score",
        ...(dealer.is_lite ? {
          meta: {
            ...defaultMeta,
            showHeaderTooltip: false,
            customHeaderRender: (
              <ResultScoreCustomHeader
                title="Deal Rating"
                tooltip={
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="10px"
                    padding="10px"
                  >
                    <Text>
                      Green if a vehicle has a Good or Great rating.
                    </Text>
                    <Text>
                      Yellow if vehicle has a Fair rating.
                    </Text>
                    <Text>
                      Red if vehicle has a High Price or Unknown rating.
                    </Text>
                  </Box>
                }
              />
            ),
          },
        } : {
          meta: {
            ...defaultMeta,
          }
        })
      }),
    ]
    : []),
  ...(!dealer.is_lite ? [
       columnHelper.accessor("price_score", {
        cell: (info) => (
          <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
        ),
        header: "Pricing",
        id: "price_score",
        meta: {
          ...defaultMeta,
        }
      }),
  ] : []),
  columnHelper.accessor("leads_score", {
    cell: (info) => (
      <ScoreBadge color={resultScoreConverterToString(info.getValue())} />
    ),
    header: "Leads",
    id: "leads_score",
    ...(dealer.is_lite ? {
      meta: {
        ...defaultMeta,
        showHeaderTooltip: false,
        customHeaderRender: (
          <ResultScoreCustomHeader
            title="Leads"
            tooltip={
              <Box
                display="flex"
                flexDirection="column"
                gap="10px"
                padding="10px"
              >
                <Text>
                  Green if vehicle's leads/vdp is 1.50% or above.
                </Text>
                <Text>
                  Yellow if vehicle's leads/vdp is between .90% and 1.49%.
                </Text>
                <Text>
                  Red if vehicle's leads/vdp is below .89%.
                </Text>
              </Box>
            }
          />
        ),
      },
    } : {
      meta: {
        ...defaultMeta,
      }
    })
  }),
];

export const getPdfColumns = (
  inventory: TInventoryOptionValue,
  columnVisibility: VisibilityState,
  dealer: TDealer
): TAggregatorColumnPDF<TResultScoreDetailsResults>[] => {
  const columns: TAggregatorColumnPDF<TResultScoreDetailsResults>[] = [
    {
      label: "Stock #",
      key: "stock_number",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: "Vehicle",
      key: "vehicle",
      func(value): any {
        return value ?? "-";
      },
    },
    ...(inventory === 'used' ? [
        {
            label: "Certified",
            key: "is_certified",
            func(value): any {
                return value ? 'Yes' : 'No';
            }
        }
    ] : []) as TAggregatorColumnPDF<TResultScoreDetailsResults>[],
    {
      label: "Result Score",
      key: "result_score",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: `VDP's`,
      key: "vdps_score",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: "Merchandising",
      key: "merchandising_score",
      func(value): any {
        return value ?? "-";
      },
    },
    ...(inventory === "used"
      ? [
        {
          label: "Deal Rating",
          key: "deal_rating_score" as keyof TResultScoreDetailsResults,
          func(value: any) {
            return value ?? "-";
          },
        },
      ]
      : []),
    ...(!dealer.is_lite ? [
      {
        label: "Pricing",
        key: "price_score",
        func(value): any {
          return value ?? "-";
        },
      },
    ] : []) as TAggregatorColumnPDF<TResultScoreDetailsResults>[],
    {
      label: "Leads",
      key: "leads_score",
      func(value): any {
        return value ?? "-";
      },
    },
  ];

  return columns.filter((column) => columnVisibility[column.key] !== false);
};

export const getXLSColumns = (
  inventory: TInventoryOptionValue,
  columnVisibility: VisibilityState,
  dealer: TDealer
): TAggregatorColumnXLS<TResultScoreDetailsResults>[] => {
  const columns: TAggregatorColumnXLS<TResultScoreDetailsResults>[] = [
    {
      label: "Stock #",
      key: "stock_number",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: "Vehicle",
      key: "vehicle",
      func(value): any {
        return value ?? "-";
      },
    },
    ...(inventory === 'used' ? [
        {
            label: "Certified",
            key: "is_certified",
            func(value): any {
                return value ? 'Yes' : 'No';
            }
        }
    ] : []) as TAggregatorColumnXLS<TResultScoreDetailsResults>[],
    {
      label: "Result Score",
      key: "result_score",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: `VDP's`,
      key: "vdps_score",
      func(value): any {
        return value ?? "-";
      },
    },
    {
      label: "Merchandising",
      key: "merchandising_score",
      func(value): any {
        return value ?? "-";
      },
    },
    ...(inventory === "used"
      ? [
        {
          label: "Deal Rating",
          key: "deal_rating_score" as keyof TResultScoreDetailsResults,
          func(value: any) {
            return value ?? "-";
          },
        },
      ]
      : []),
    ...(!dealer.is_lite ? [
      {
        label: "Pricing",
        key: "price_score",
        func(value): any {
          return value ?? "-";
        },
      },
    ] : []) as TAggregatorColumnXLS<TResultScoreDetailsResults>[],
    {
      label: "Leads",
      key: "leads_score",
      func(value): any {
        return value ?? "-";
      },
    },
  ];

  return columns.filter((column) => columnVisibility[column.key] !== false);
};

export const defaultOffset = 0;
